/**
 * ChecklistRow — Two-line checklist item row
 *
 * Line 1: Status icon + title + required badge + status badge
 * Line 2: Clause ref pills + description preview + linked document count
 *
 * Single click opens the detail sheet directly (no expand/collapse).
 */

import { motion } from "framer-motion";
import {
  CheckCircle2,
  Circle,
  Loader,
  FileText,
  MessageSquare,
  AlertTriangle,
} from "lucide-react";
import { Badge } from "@/components/ui";
import { getPaletteColor } from "@/lib/color-palettes";
import { cn, useTranslation } from "@/packages/utils";
import { getSeverityStyle } from "../findings/findings-constants";
import { ClauseRefBadge } from "./ClauseRefBadge";
import type { UIChecklistItem, ChecklistItemStatus } from "@/packages/types";
import type { ChecklistFindingsSummary } from "./checklist-findings-map";

// ─── Status Config ──────────────────────────────────────────────────────────

export type ChecklistStatusConfig = {
  labelKey: string;
  dotColor: string;
  badgeBg: string;
  badgeText: string;
  /** Resolved label — populated at render time via t() */
  label: string;
};

/** Returns checklist status colors from palette (resolved lazily at call time). */
function getChecklistStatusConfig(): Record<
  ChecklistItemStatus,
  Omit<ChecklistStatusConfig, "label"> & { label: string }
> {
  const missingColor = getPaletteColor("audit-status", 6);
  const uploadedColor = getPaletteColor("audit-status", 2);
  const approvedColor = getPaletteColor("audit-status", 4);
  return {
    MISSING: {
      labelKey: "checklist.status.missing",
      label: "Missing",
      dotColor: missingColor,
      badgeBg: `${missingColor}26`,
      badgeText: missingColor,
    },
    UPLOADED: {
      labelKey: "checklist.status.uploaded",
      label: "Uploaded",
      dotColor: uploadedColor,
      badgeBg: `${uploadedColor}26`,
      badgeText: uploadedColor,
    },
    APPROVED: {
      labelKey: "checklist.status.approved",
      label: "Approved",
      dotColor: approvedColor,
      badgeBg: `${approvedColor}26`,
      badgeText: approvedColor,
    },
  };
}

/**
 * Non-reactive config — resolves palette colors at call time.
 * For translated labels, use `useChecklistStatusConfig()` hook instead.
 */
export function getChecklistStatusConfigStatic() {
  return getChecklistStatusConfig();
}

/** @deprecated Use getChecklistStatusConfigStatic() instead. Proxy-backed for backward compat. */
export const CHECKLIST_STATUS_CONFIG = new Proxy(
  {} as Record<
    ChecklistItemStatus,
    Omit<ChecklistStatusConfig, "label"> & { label: string }
  >,
  {
    get(_target, prop: string) {
      return getChecklistStatusConfig()[prop as ChecklistItemStatus];
    },
    ownKeys() {
      return ["MISSING", "UPLOADED", "APPROVED"];
    },
    getOwnPropertyDescriptor(_target, prop: string) {
      if (["MISSING", "UPLOADED", "APPROVED"].includes(prop)) {
        return {
          configurable: true,
          enumerable: true,
          writable: true,
          value: getChecklistStatusConfig()[prop as ChecklistItemStatus],
        };
      }
      return undefined;
    },
  },
);

/**
 * Hook that returns status config with translated labels.
 */
export function useChecklistStatusConfig(): Record<
  ChecklistItemStatus,
  ChecklistStatusConfig
> {
  const { t } = useTranslation("audits");
  const base = getChecklistStatusConfig();
  return {
    MISSING: {
      ...base.MISSING,
      label: t(base.MISSING.labelKey as "checklist.status.missing") as string,
    },
    UPLOADED: {
      ...base.UPLOADED,
      label: t(base.UPLOADED.labelKey as "checklist.status.uploaded") as string,
    },
    APPROVED: {
      ...base.APPROVED,
      label: t(base.APPROVED.labelKey as "checklist.status.approved") as string,
    },
  };
}

// ─── Status Icon ────────────────────────────────────────────────────────────

function StatusIcon({ status }: { status: ChecklistItemStatus }) {
  switch (status) {
    case "APPROVED":
      return (
        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
          <CheckCircle2 size={14} className="text-emerald-500" />
        </div>
      );
    case "UPLOADED":
      return (
        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-soft">
          <Loader size={14} className="text-accent" />
        </div>
      );
    default:
      return (
        <div className="flex h-5 w-5 shrink-0 items-center justify-center">
          <Circle size={14} className="text-muted" />
        </div>
      );
  }
}

function FindingsBadge({ summary }: { summary: ChecklistFindingsSummary }) {
  const { t } = useTranslation("audits");
  const severityStyle = getSeverityStyle(summary.worstSeverity);
  const severityLabel = (
    t(severityStyle.labelKey as "findings.severity.critical") as string
  ).toLocaleLowerCase();

  const label =
    summary.count === 1
      ? (t("checklist.row.oneFinding" as "checklist.item.required", {
          worstSeverity: severityLabel,
        }) as string)
      : summary.worstCount === summary.count
        ? (t(
            "checklist.row.nFindingsSameSeverity" as "checklist.item.required",
            {
              count: summary.count,
              worstSeverity: severityLabel,
            },
          ) as string)
        : (t("checklist.row.nFindings" as "checklist.item.required", {
            count: summary.count,
            worstCount: summary.worstCount,
            worstSeverity: severityLabel,
          }) as string);

  return (
    <Badge
      color={{
        bg: `${severityStyle.border}1a`,
        text: severityStyle.border,
      }}
      className="px-2 py-0.5 text-[10px] flex items-center gap-1"
    >
      <AlertTriangle size={10} />
      {label}
    </Badge>
  );
}

// ─── Description Preview ─────────────────────────────────────────────────────

function extractInfoReportPreview(raw: string): string | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "sections" in parsed &&
      Array.isArray((parsed as { sections: unknown[] }).sections)
    ) {
      const sections = (parsed as { sections: { content?: string }[] })
        .sections;
      const first = sections[0];
      if (first?.content) return first.content;
    }
  } catch {
    // not JSON — return as-is if it doesn't look like JSON
    if (!raw.trimStart().startsWith("{")) return raw;
  }
  return null;
}

function extractSummaryReportPreview(raw: string): string | null {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed.verdict === "string") return parsed.verdict;
    if (typeof parsed.reasoning === "string") return parsed.reasoning;
    if (typeof parsed.summary === "string") return parsed.summary;
    // Last resort: show compliance status as a short label
    if (typeof parsed.complianceStatus === "string")
      return parsed.complianceStatus;
  } catch {
    // not JSON — use as plain text
    if (!raw.trimStart().startsWith("{")) return raw;
  }
  return null;
}

function getDescriptionPreview(item: UIChecklistItem): string | null {
  if (item.description) return item.description;
  if (item.informationReport) {
    const preview = extractInfoReportPreview(item.informationReport);
    if (preview) return preview;
  }
  if (item.summaryReport) {
    return extractSummaryReportPreview(item.summaryReport) ?? null;
  }
  return null;
}

// ─── Checklist Row ──────────────────────────────────────────────────────────

export interface ChecklistRowProps {
  item: UIChecklistItem;
  index: number;
  onOpenDetail: () => void;
  findingsSummary?: ChecklistFindingsSummary;
}

export function ChecklistRow({
  item,
  index,
  onOpenDetail,
  findingsSummary,
}: ChecklistRowProps) {
  const { t } = useTranslation("audits");
  const statusConfigs = useChecklistStatusConfig();
  const statusConfig = statusConfigs[item.status];

  const clauseRefs = item.clauseDetails?.map((c) => c.clauseNumber) ?? [];
  const descriptionPreview = getDescriptionPreview(item);
  const truncatedDescription = descriptionPreview
    ? descriptionPreview.slice(0, 80) +
      (descriptionPreview.length > 80 ? "\u2026" : "")
    : null;
  const linkedDocCount = item.linkedDocumentIds?.length ?? 0;
  const docCountLabel =
    linkedDocCount === 1
      ? (t("checklist.row.oneDocument" as "checklist.item.required") as string)
      : (t("checklist.row.nDocuments" as "checklist.item.required", {
          count: linkedDocCount,
        }) as string);

  const totalQuestions = item.questions?.length ?? 0;
  const answeredOrders = new Set(
    (item.answers ?? []).map((a) => a.question.order),
  );
  const answeredQuestions = answeredOrders.size;

  const hasSecondLine =
    clauseRefs.length > 0 ||
    truncatedDescription ||
    linkedDocCount > 0 ||
    totalQuestions > 0;

  const isExcluded = item.soaExcluded === true;

  return (
    <motion.div
      className={cn(
        "rounded-xl bg-card border-l-2 border-l-transparent hover:border-l-accent cursor-pointer transition-colors",
        isExcluded && "opacity-40 pointer-events-none",
      )}
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: isExcluded ? 0.4 : 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      onClick={(e) => {
        // Don't open detail sheet when clicking clause ref popovers
        if ((e.target as HTMLElement).closest("button[data-clause-ref]"))
          return;
        onOpenDetail();
      }}
    >
      <div className="px-4 py-3 flex flex-col gap-1">
        {/* Line 1: status icon + title + badges */}
        <div className="flex items-center gap-3">
          <StatusIcon status={item.status} />
          <p
            className={`text-sm font-semibold flex-1 truncate ${
              item.status === "APPROVED"
                ? "text-secondary line-through"
                : "text-primary"
            }`}
          >
            {item.name}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            {findingsSummary && <FindingsBadge summary={findingsSummary} />}
            {isExcluded && (
              <Badge
                color={{ bg: "#94a3b81a", text: "#94a3b8" }}
                className="px-2 py-0.5 text-[10px]"
              >
                {t("checklist.item.soaExcluded" as "checklist.item.required")}
              </Badge>
            )}
            <Badge
              color={{
                bg: statusConfig.badgeBg,
                text: statusConfig.badgeText,
              }}
              className="w-[84px] text-center"
            >
              {statusConfig.label}
            </Badge>
          </div>
        </div>

        {/* Line 2: clause refs + description + doc count */}
        {hasSecondLine && (
          <div className="flex items-center gap-2 pl-8 flex-wrap">
            {clauseRefs.map((ref) => {
              const detail = item.clauseDetails?.find(
                (c) => c.clauseNumber === ref,
              );
              return (
                <ClauseRefBadge
                  key={ref}
                  clauseRef={ref}
                  clauseInfo={
                    detail
                      ? {
                          standard: detail.standard ?? "",
                          title: detail.title,
                          description: detail.description ?? "",
                        }
                      : undefined
                  }
                />
              );
            })}
            {truncatedDescription && (
              <>
                {clauseRefs.length > 0 && (
                  <span className="text-muted text-[11px]">&middot;</span>
                )}
                <span className="text-xs text-muted-foreground truncate max-w-[280px]">
                  {truncatedDescription}
                </span>
              </>
            )}
            {totalQuestions > 0 && (
              <span className="flex items-center gap-1.5 shrink-0">
                <MessageSquare size={11} className="text-muted-foreground" />
                <div className="h-1 w-12 bg-border/50 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full bg-accent"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${Math.round((answeredQuestions / totalQuestions) * 100)}%`,
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  />
                </div>
                <span className="text-[11px] text-muted tabular-nums">
                  {answeredQuestions}/{totalQuestions}
                </span>
              </span>
            )}
            {linkedDocCount > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto shrink-0">
                <FileText size={11} />
                {docCountLabel}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
